import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Annotation from "../../model/Annotation";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAlerts } from "../../contexts/AlertsContext";
import UploadManager from "../../shared/UploadManager";
import APIEndPoints from "../../utils/APIEndPoints";
import AnnotationType from "../../model/enums/AnnotationType";
import AnnotationInfo from "../../model/AnnotationInfo";
import AnnotationInfoType from "../../model/enums/AnnotationInfoType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import { IApiClientError } from "../../shared/ApiClient";
import UploadFileType from "../../model/enums/UploadFileType";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import FormInputImage from "../common/FormInputImage";
import ImgUtil from "../../utils/ImgUtil";
import FormTextArea from "../common/FormTextArea";
import FormDropDown from "../common/FormDropDown";
import FormInputFile from "../common/FormInputFile";
import APIError from "../../utils/APIError";
import FormDownloadFile from "../common/FormDownloadFile";
import { FaInfoCircle } from "react-icons/fa";
import UploadManagerProgressReport from "../../model/UploadManagerProgressReport";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface IAnnotationFormErrors {
  name: string;
  description: string;
  type: string;
  shape_and_context: string;
}

const defaultFormErrors: IAnnotationFormErrors = {
  name: "",
  description: "",
  type: "",
  shape_and_context: "",
};

function AnnotationsAddEdit() {
  // read the IDs from the url
  const { e3dinstid, id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage uploading & creating animations
  const [uploading, setUploading] = useState(false);
  // manage upload progress display
  const [uploadProgress, setUploadProgress] = useState<
    UploadManagerProgressReport | undefined
  >(undefined);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<Annotation>(
    Annotation.EmptyInstance(),
  );
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IAnnotationFormErrors>(defaultFormErrors);

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const updateUploadProgress = (info: UploadManagerProgressReport) => {
    setUploadProgress(info);
  };
  const [uploadManager] = useState(
    new UploadManager({
      apiClient: apiClient,
      progressCallback: updateUploadProgress,
    }),
  );

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let annotation: Annotation | undefined = undefined;
        if (id) {
          annotation = await apiClient?.get<Annotation>({
            url: APIEndPoints.ANNOTATIONS.ONE(parseInt(id)),
          });
        }

        // unify initial model with data from all sources
        const currentData = annotation
          ? annotation
          : Annotation.EmptyInstance();

        // set into the state all the information
        setFormData(currentData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(formData);

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    var shapesServerId: number | undefined = formData.shape_and_context;
    var infoImagesServerIDs: Array<number> = [];
    var infoSoundsServerIDs: Array<number> = [];
    try {
      setUploading(true);
      // upload the files
      const uploadResult = await uploadManager.uploadFiles();

      if (uploadResult instanceof Array) {
        uploadResult.forEach((result) => {
          if (result.identifier === "icon") {
            iconServerId = result.serverId;
          } else if (
            result.identifier === "shape_and_context" &&
            result.serverId
          ) {
            shapesServerId = result.serverId;
          } else if (
            result.identifier === "ui_selected_images" &&
            result.serverId
          ) {
            infoImagesServerIDs.push(result.serverId);
          } else if (
            result.identifier === "ui_selected_sounds" &&
            result.serverId
          ) {
            infoSoundsServerIDs.push(result.serverId);
          }
        });
      }
    } catch (exception) {
      console.log(exception);
    }

    // merge the annotation info files into a single array
    var annotation_infos: AnnotationInfo[] = [];
    infoImagesServerIDs.map((id) =>
      annotation_infos.push(new AnnotationInfo(id, AnnotationInfoType.IMAGE)),
    );
    infoSoundsServerIDs.map((id) =>
      annotation_infos.push(new AnnotationInfo(id, AnnotationInfoType.SOUND)),
    );

    var body = new Annotation(
      "",
      formData.description,
      iconServerId,
      shapesServerId,
      AnnotationType._3D,
      annotation_infos,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.ANNOTATIONS.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Annotation updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.ANNOTATIONS,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    } else {
      apiClient
        ?.post<Annotation>({
          url: APIEndPoints.ANNOTATIONS.NEW(parseInt(e3dinstid!)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Annotation created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.ANNOTATIONS,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.TEXTURES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Annotation deleted successfully",
        });
        navigate(
          SETTINGS.go_back({
            section: AppSections._3DINSTANCES,
            id: formData.id_3dinstance,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the annotation",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as Annotation);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    setFormData(formDataCopy as Annotation);
  };

  const formFileChange = (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    let display = "";

    uploadManager.replaceFiles(fieldId, UploadFileType.ANNOTATION, files);

    // process the display, based on the name of the files
    if (files) {
      for (let i = 0; i < files.length; i++) {
        display += display === "" ? files[i].name : ", " + files[i].name;
      }
    }

    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[keyForDisplay] = display === "" ? undefined : display;
    if (keyForData) {
      formDataCopy[keyForData] = -1;
    }
    setFormData(formDataCopy as Annotation);
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: AppSections._3DINSTANCES,
        id: formData.id_3dinstance ?? parseInt(e3dinstid!),
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const annotationTypeOptions = Object.entries(AnnotationType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  annotationTypeOptions.splice(0, 0, {
    value: "",
    label: "-- please select annotation type --",
  });

  const pageTitle = id ? "Edit annotation" : "Create a new annotation";
  const confirmLabel = id ? "Update annotation" : "Create annotation";

  const deleteButton = id ? (
    <Button
      label="Delete annotation"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this annotation?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete annotation",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading || uploading) {
    return (
      <Loading
        uploading={uploading}
        uploadingMessage="Please wait. Uploading annotation files..."
        uploadingProgress={uploadProgress}
      />
    );
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      {/* <FormInputText
        label="Annotation name"
        id="name"
        value={formData.name}
        required={true}
        placeholder="Name of annotation"
        onChange={formInputChange}
        error={formErrors.name}
      /> */}

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
      />

      <FormDropDown
        label="Annotation type"
        id="type"
        value={formData.type}
        required={true}
        options={annotationTypeOptions}
        onChange={formInputChange}
        error={formErrors.type}
      />

      {!id && (
        <FormInputFile
          label="Shape and Context file"
          id="shape_and_context"
          keyForData="shape_and_context"
          keyForDisplay="ui_shape_and_context"
          value={formData.ui_shape_and_context}
          required={true}
          disabled={!!id}
          acceptedFileTypes=".json"
          onChange={formFileChange}
          error={formErrors.shape_and_context}
        />
      )}

      {id && (
        <FormDownloadFile
          label="Shape and Context file"
          file_name="shape_and_context.json"
          file_upload_id={formData.shape_and_context!}
          warning_message="You cannot change this entry. Updated versions should be published as
        new annotations."
        />
      )}

      {!id && (
        <>
          <FormInputFile
            label="Image files"
            id="ui_selected_images"
            keyForDisplay="ui_selected_images"
            value={formData.ui_selected_images}
            required={false}
            disabled={!!id}
            multipleFiles={true}
            acceptedFileTypes=".png, .jpg, .tiff"
            onChange={formFileChange}
          />

          <FormInputFile
            label="Sound files"
            id="ui_selected_sounds"
            keyForDisplay="ui_selected_sounds"
            value={formData.ui_selected_sounds}
            required={false}
            disabled={!!id}
            multipleFiles={true}
            acceptedFileTypes=".wav, .mp3, .mp4"
            onChange={formFileChange}
          />
        </>
      )}
      {id &&
        Array.isArray(formData.annotation_infos) &&
        formData.annotation_infos.length > 0 && (
          <label className="form-label">Annotation files:</label>
        )}

      {id &&
        Array.isArray(formData.annotation_infos) &&
        formData.annotation_infos.map((file) => {
          return (
            <FormDownloadFile
              key={file.id}
              file_name={file.annotation_info_name}
              file_upload_id={file.file!}
            />
          );
        })}

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(formData: Annotation): IAnnotationFormErrors {
  let errors: IAnnotationFormErrors = {
    name: "",
    description: "",
    type: "",
    shape_and_context: "",
  };

  errors.name =
    // formData.name.length < 4
    //   ? "Annotation name should be at least 4 characters long"
    //   : "";
    errors.description =
      formData.description.length < 10
        ? "Annotation description should be at least 10 characters long"
        : "";
  errors.type = !formData.type
    ? "Please select the type of this annotation"
    : "";
  errors.shape_and_context = !formData.shape_and_context
    ? "Please select the JSON file that describes this annotation"
    : "";

  return errors;
}

function isFormInvalid(errors: IAnnotationFormErrors): boolean {
  return (
    errors.name !== "" ||
    errors.description !== "" ||
    errors.type !== "" ||
    errors.shape_and_context !== ""
  );
}

export default AnnotationsAddEdit;
